import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { ChevronLeft, ChevronRight, ChevronDown } from 'react-feather';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { graphql, useStaticQuery } from 'gatsby';
import { isEmpty } from 'lodash';
import { Direction, Matcher, SortType, useSearchActions, useSearchState } from '@yext/search-headless-react';
import { SearchAnalyticsEventType, provideSearchAnalytics } from '@yext/analytics';
import CustomSearchBar from './searchbar';
import { Button, Container, Heading, InternalLink, Label, Text, animation, colors, media } from '../../ui-components';
import * as searchUtils from '../../utils/search-utils';

const searchPropTypes = {
  geoSpace: PropTypes.string
};

const searchFacetPropTypes = {
  fieldId: PropTypes.string,
  displayName: PropTypes.string,
  toggleVisibility: PropTypes.func,
  isVisible: PropTypes.bool,
  resultCount: PropTypes.number,
  setResultCount: PropTypes.func
};

const searchResultCardPropTypes = {
  result: PropTypes.shape({
    entityType: PropTypes.string,
    rawData: PropTypes.shape({
      name: PropTypes.string,
      // eslint-disable-next-line camelcase
      c_snippet: PropTypes.string,
      slug: PropTypes.string,
      // eslint-disable-next-line camelcase
      c_localizationSlug: PropTypes.string
    })
  })
};

const sortByOptions = [
  {
    label: 'Most Relevant',
    verticals: [
      searchUtils.VERTICAL_ALL_CONTENT,
      searchUtils.VERTICAL_CASE_STUDIES,
      searchUtils.VERTICAL_INSIGHTS,
      searchUtils.VERTICAL_SERVICES,
      searchUtils.VERTICAL_INDUSTRIES,
      searchUtils.VERTICAL_PARTNERSHIPS
    ],
    sortBy: {
      direction: Direction.Descending,
      type: SortType.Relevance
    }
  },
  {
    label: 'Latest',
    verticals: [searchUtils.VERTICAL_INSIGHTS],
    sortBy: {
      field: 'c_publishDate',
      direction: Direction.Descending,
      type: SortType.Field
    }
  },
  {
    label: 'Latest',
    verticals: [searchUtils.VERTICAL_CASE_STUDIES],
    sortBy: {
      field: 'c_createdAt',
      direction: Direction.Descending,
      type: SortType.Field
    }
  }
];

// Maximum number of search results to display on a single page
const MAX_RESULTS_PER_PAGE = 10;

const SearchComponent = props => {
  const { geoSpace } = props;

  let urlParams = new URLSearchParams(typeof window !== 'undefined' && window.location.search);
  const searchActions = useSearchActions();

  /**
   * Uses a state variable that is set when a search is triggered instead of subscribing to the
   * search state query because autocomplete sets the search state query causing the heading to
   * live-update. Once query has been set using setQuery, we know when a search has been triggered
   * so query is also used to conditionally render some of the search components.
   */
  const [query, setQuery] = useState('');

  // Keeps track of which filters are visible
  const [isFilterByVisible, setIsFilterByVisible] = useState(true);
  const [isSortByVisible, setIsSortByVisible] = useState(false);
  const [isCategoryVisible, setIsCategoryVisible] = useState(false);
  const [isInsightTypeVisible, setIsInsightTypeVisible] = useState(false);
  const [isToolVisible, setIsToolVisible] = useState(false);

  const toggleFilterBy = () => setIsFilterByVisible(!isFilterByVisible);
  const toggleSortBy = () => setIsSortByVisible(!isSortByVisible);
  const toggleCategory = () => setIsCategoryVisible(!isCategoryVisible);
  const toggleInsightType = () => setIsInsightTypeVisible(!isInsightTypeVisible);
  const toggleTool = () => setIsToolVisible(!isToolVisible);

  // Paginate any extra facets
  const [categoryCount, setCategoryCount] = useState(10);
  const [insightTypeCount, setInsightTypeCount] = useState(10);
  const [toolCount, setToolCount] = useState(10);

  // Keeps track of the currently applied search filter (vertical)
  const [filterApplied, setFilterApplied] = useState(searchUtils.VERTICAL_ALL_CONTENT);

  // Whether a search is currently being performed or not
  const loading = useSearchState(state => state.searchStatus.isLoading) ?? false;

  /**
   * Subscribes to the vertical results from the search state so we know when a vertical search has
   * been performed.
   */
  const verticalResults = useSearchState(state => state.vertical.results) ?? null;
  const resultsTotal = useSearchState(state => state.vertical.resultsCount) ?? 0;

  // This is for limiting search results that are too large. Setting a cap at 50 but still grabbing the resultsTotal.
  const defaultResultsTotal = resultsTotal > 50 ? 50 : resultsTotal;

  const [displayedResults, setDisplayedResults] = useState([]);

  // Keeps track of the current results page number
  const [pageNumber, setPageNumber] = useState(1);

  // Default search caps results at 50
  const [defaultSearchActive, setDefaultSearchActive] = useState(true);

  // Keeps track of whether searchbar is currently in use
  const [searchActive, setSearchActive] = useState(false);

  /**
   * Adding this here for future reference if it is desired, this is NOT needed to enabled the search
   * analytics below, this is only to enable search session tracking. Without this, our analytics in
   * Yext will show # of searches equivalent to # of search sessions which is fine, but if we want to
   * gain more insight into number of search sessions, we will need to enable this. Enabling this does
   * add a cookie, session-id, to the site and will need to be handled within OneTrust.
   */
  // searchActions.setSessionTrackingEnabled(true);

  const analytics = provideSearchAnalytics({
    apiKey: process.env.YEXT_API_KEY,
    experienceKey: process.env.YEXT_EXPERIENCE_KEY,
    businessId: process.env.YEXT_BUSINESS_ID
  });

  const mostRecentQueryId = useSearchState(state => state.query.queryId) ?? '';

  const reportSearchResultClick = result => {
    const slug = result?.rawData?.slug;
    const fullSlug = result?.rawData?.c_fullSlug;

    analytics.report({
      type: SearchAnalyticsEventType.CtaClick,
      verticalKey: filterApplied,
      entityId: result.id,
      searcher: 'VERTICAL',
      queryId: mostRecentQueryId,
      url:
        result.entityType == searchUtils.ENTITY_TYPE_CASE_STUDY
          ? `/case-studies/${slug}`
          : result.entityType == searchUtils.ENTITY_TYPE_SERVICE
          ? `/services/${fullSlug}`
          : result.entityType == searchUtils.ENTITY_TYPE_INDUSTRY
          ? `/industries/${slug}`
          : result.entityType == searchUtils.ENTITY_TYPE_PARTNERSHIP
          ? `/partnerships/${slug}`
          : `/insights/${slug}`
    });
  };

  /**
   * Makes the necessary preparations before executing a vertical search. Using
   * `searchActions.setVertical()` resets the configurations so this function must be called every
   * time a search is to be executed on a new vertical.
   *
   * @param {string} vertical - the vertical to be searched
   */
  const prepareVerticalSearch = vertical => {
    searchActions.setVertical(vertical);
    searchActions.setVerticalLimit(MAX_RESULTS_PER_PAGE);
    // Sorts by 'Most Relevant' by default
    searchActions.setSortBys([sortByOptions[0].sortBy]);
    // Set static filters to only retrieve GLOBAL and locale-specific results
    searchUtils.setGeoStaticFilters(searchActions, geoSpace);
  };

  const executeVerticalSearch = vertical => {
    // Below verticals have a low total and shouldn't be limited if their counts go above 50
    if (vertical === searchUtils.VERTICAL_INDUSTRIES || vertical === searchUtils.VERTICAL_PARTNERSHIPS || vertical === searchUtils.VERTICAL_SERVICES) {
      setDefaultSearchActive(false)
    } else {
      setDefaultSearchActive(true)
    }

    setPageNumber(1);
    setFilterApplied(vertical);

    setIsFilterByVisible(true);
    setIsSortByVisible(false);

    setIsCategoryVisible(false);
    setCategoryCount(10);
    setIsInsightTypeVisible(false);
    setInsightTypeCount(10);
    setIsToolVisible(false);
    setToolCount(10);

    urlParams.set(searchUtils.URL_PARAMS_FILTER, vertical);
    urlParams.delete(searchUtils.URL_PARAMS_FACET_TAG);
    urlParams.delete(searchUtils.URL_PARAMS_PAGE);
    prepareVerticalSearch(vertical);
    searchActions.executeVerticalQuery();
    history.pushState(null, '', '?' + urlParams.toString());
  };

  /**
   * Attempts to handle an extreme edge case involving localization slugs (LSlugs). When a case
   * study/insight has an LSlug set, the global page that the LSlug points to is not generated for
   * that locale. Therefore, we do not want those results to be visible because it would cause a 404
   * if a user clicks on it. Currently, there is no way to filter these results before a search
   * occurs, so this filtering is done on the fly but only applies if the two results (the localized
   * result and the global result) appear in the same ‘result set’ of 10 results (since that’s how
   * many results we show on a page) returned by Yext.
   *
   * !NOTE: This does not handle localization slugs any other time. If a global result is returned
   * !separately from the localized result, it will still appear and will still cause a 404 if
   * !clicked on. This is just unfortunately due to us not being able pre-filter results at this time
   * !given our current configurations.
   *
   */
  useEffect(() => {
    if (isEmpty(verticalResults)) {
      if (!searchActive) {
        setDisplayedResults(null);
      }
    } else {
      // Get results that have a localization slug set
      const localizedResults = verticalResults.filter(result => result.rawData?.c_localizationSlug);

      // Filter the results to remove any results in the GLOBAL space whose slug is equal to a localized slug that was found
      const filteredResults = verticalResults.filter(result => {
        const shouldExclude = localizedResults.some(localizedResult => {
          return localizedResult.rawData?.c_localizationSlug === result.rawData?.slug && result.rawData?.c_locale === 'GLOBAL';
        });

        return !shouldExclude;
      });

      setDisplayedResults(filteredResults);
    }
  }, [verticalResults]);

  /**
   * On render, executes a vertical search if a query is found in the URL i.e., the user executes a
   * search using the search bar from the navigation menu and is navigated to the search page).
   *
   * If a filter (vertical) is also found in the URL (i.e., the user copies a search results link and
   * sends it to someone, or if the user navigates backwards after clicking on a result) that vertical
   * is used and applies any valid facets/page offset that are found as well.
   */
  useEffect(() => {
    const urlQuery = urlParams.get(searchUtils.URL_PARAMS_QUERY);
    const urlFilter = urlParams.get(searchUtils.URL_PARAMS_FILTER);
    const urlFacetOptions = urlParams.getAll(searchUtils.URL_PARAMS_FACET_TAG);
    const urlPage = parseInt(urlParams.get(searchUtils.URL_PARAMS_PAGE));

    const executeInitialVerticalSearch = async () => {
      // Set vertical to 'All Content' by default if filter in URL is not valid
      let vertical =
        urlFilter === searchUtils.VERTICAL_CASE_STUDIES ||
        urlFilter === searchUtils.VERTICAL_INSIGHTS ||
        urlFilter === searchUtils.VERTICAL_SERVICES ||
        urlFilter === searchUtils.VERTICAL_INDUSTRIES ||
        urlFilter === searchUtils.VERTICAL_PARTNERSHIPS
          ? urlFilter
          : searchUtils.VERTICAL_ALL_CONTENT;
      setFilterApplied(vertical);
      prepareVerticalSearch(vertical);
      let response = await searchActions.executeVerticalQuery();

      /**
       * Facets don't exist until a vertical query is executed so we await the above vertical query
       * and then apply any valid facet options that are found and execute another vertical query
       * for them to take effect.
       */
      if (
        (vertical === searchUtils.VERTICAL_INSIGHTS || vertical === searchUtils.VERTICAL_CASE_STUDIES) &&
        urlFacetOptions.length > 0 &&
        response.facets
      ) {
        const verticalFacets = vertical === searchUtils.VERTICAL_CASE_STUDIES
          ? [searchUtils.FACET_CASE_STUDIES_CATEGORY, searchUtils.FACET_CASE_STUDIES_TOOL]
          : [searchUtils.FACET_INSIGHTS_INSIGHT_TYPE];

        let currentFacets = verticalFacets.map(vf => response.facets.find(f => f.fieldId === vf));

        for (const option of urlFacetOptions) {
          let optionFound = false;
          for (const currentFacet in currentFacets) {
            // Check to see if the facet option found in the URL is valid for the current facet and
            // apply it, otherwise just remove it from the URL
            if (currentFacet.options.find(o => o.value === option)) {
              searchActions.setFacetOption(currentFacet.fieldId, { matcher: Matcher.Equals, value: option }, true);
              optionFound = true;
            }
          }
          if (!optionFound) {
            urlParams.delete(searchUtils.URL_PARAMS_FACET_TAG, option);
          }
        }

        response = await searchActions.executeVerticalQuery();
      } else {
        // Removes all facet tags from URL if vertical does not support facets (anything but case studies and insights)
        urlParams.delete(searchUtils.URL_PARAMS_FACET_TAG);
      }

      /**
       * If a valid page number is parsed from the URL, makes sure that it is positive and does not
       * exceed the maximum amount of pages. Facets are applied first and a search is executed and
       * awaited to get an accurate max page count
       * Yext Offset = (current page number - 1) * vertical limit
       */
      if (urlPage) {
        let page = Math.max(
          1,
          Math.min(urlPage, Math.ceil(response.verticalResults.resultsCount / MAX_RESULTS_PER_PAGE))
        );
        searchActions.setOffset((page - 1) * MAX_RESULTS_PER_PAGE);
        setPageNumber(page);
        urlParams.set(searchUtils.URL_PARAMS_PAGE, page);

        searchActions.executeVerticalQuery();
      } else {
        urlParams.delete(searchUtils.URL_PARAMS_PAGE);
      }

      history.pushState(null, '', '?' + urlParams.toString());
    };

    if (urlQuery) {
      searchActions.setQuery(urlQuery);
      setQuery(urlQuery);

      executeInitialVerticalSearch();
    } else {
      // If there's no query param, just reset the search params
      urlParams = new URLSearchParams();
      history.pushState(null, '', '?' + urlParams.toString());
    }
  }, []);

  const handleSearch = ({ query }) => {
    setQuery(query);

    if (query) {
      urlParams.set(searchUtils.URL_PARAMS_QUERY, query);
      searchActions.setQuery(query);
      executeVerticalSearch(searchUtils.VERTICAL_ALL_CONTENT);
    } else {
      // Clear the search params the search bar is cleared
      urlParams = new URLSearchParams();
      history.pushState(null, '', '?' + urlParams.toString());
    }
  };

  const SearchFilters = () => {
    const filterOptions = [
      {
        label: 'All Results',
        filter: searchUtils.VERTICAL_ALL_CONTENT
      },
      {
        label: 'Insights',
        filter: searchUtils.VERTICAL_INSIGHTS
      },
      {
        label: 'Case Studies',
        filter: searchUtils.VERTICAL_CASE_STUDIES
      },
      {
        label: 'Services',
        filter: searchUtils.VERTICAL_SERVICES
      },
      {
        label: 'Industries',
        filter: searchUtils.VERTICAL_INDUSTRIES
      },
      {
        label: 'Partnerships',
        filter: searchUtils.VERTICAL_PARTNERSHIPS
      }
    ];

    const handleFilterClick = filter => {
      // Only perform a new search when a new vertical is selected
      if (filter !== filterApplied) {
        executeVerticalSearch(filter);
      }
    };

    return (
      <>
        <FilterLabel size={'small'} onClick={toggleFilterBy}>
          {isFilterByVisible ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {'Filter By'}
        </FilterLabel>
        <FilterOptionsList isVisible={isFilterByVisible}>
          {filterOptions.map(f => (
            <FilterOptionButton
              key={f.label}
              buttonstyle={'secondary'}
              onClick={() => handleFilterClick(f.filter)}
              selected={filterApplied == f.filter}
            >
              <Text mobileSize={'small'}>{f.label}</Text>
            </FilterOptionButton>
          ))}
        </FilterOptionsList>
      </>
    );
  };

  const SearchSort = () => {
    // sortBys contains all the sortBy options that are in the search state
    const sortBys = useSearchState(state => state.vertical.sortBys);
    // selectedSort is the currently selected sortBy option
    const selectedSort = sortByOptions.find(s => s.sortBy === sortBys?.[0]) || sortByOptions[0];

    const handleSortClick = sortBy => {
      setPageNumber(1);
      urlParams.delete(searchUtils.URL_PARAMS_PAGE);
      history.pushState(null, '', '?' + urlParams.toString());

      searchActions.setOffset(0);
      searchActions.setSortBys([sortBy]);
      searchActions.executeVerticalQuery();
    };

    return (
      <>
        <FilterLabel size={'small'} onClick={toggleSortBy}>
          {isSortByVisible ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {'Sort By'}
        </FilterLabel>
        <FilterOptionsList isVisible={isSortByVisible}>
          {sortByOptions.map(
            s =>
              s.verticals.includes(filterApplied) && (
                <FilterOptionButton
                  key={s.label}
                  buttonstyle={'secondary'}
                  onClick={() => handleSortClick(s.sortBy)}
                  selected={s.sortBy === selectedSort?.sortBy}
                >
                  <Text mobileSize={'small'}>{s.label}</Text>
                </FilterOptionButton>
              )
          )}
        </FilterOptionsList>
      </>
    );
  };

  const SearchFacet = ({ fieldId, displayName, toggleVisibility, isVisible, resultCount, setResultCount }) => {
    const facet = useSearchState(state => state.filters.facets?.find(f => f.fieldId === fieldId));

    const handleFacetClick = (value, selected) => {
      if (selected) {
        urlParams.append(searchUtils.URL_PARAMS_FACET_TAG, value);
      } else {
        urlParams.delete(searchUtils.URL_PARAMS_FACET_TAG, value);
      }

      setPageNumber(1);
      urlParams.delete(searchUtils.URL_PARAMS_PAGE);
      history.pushState(null, '', '?' + urlParams.toString());

      searchActions.setOffset(0);
      searchActions.setFacetOption(fieldId, { matcher: Matcher.Equals, value }, selected);
      searchActions.executeVerticalQuery();
    };

    return facet && facet.options.length > 0 ? (
      <>
        <FilterLabel size="small" onClick={toggleVisibility}>
          {isVisible ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {displayName}
        </FilterLabel>
        <FilterOptionsList isVisible={isVisible}>
          {facet.options.slice(0, resultCount).map((option, i) => (
            <FilterOptionButton
              key={`${fieldId}_${i}`}
              buttonstyle={'secondary'}
              selected={option.selected}
              onClick={() => {
                handleFacetClick(option.value, !option.selected);
              }}
            >
              <Text mobileSize={'small'}>{`${option.displayName} (${option.count})`}</Text>
            </FilterOptionButton>
          ))}
          {resultCount < facet.options.length && <FilterOptionButton buttonstyle={'secondary'} onClick={() => setResultCount(Math.min(facet.options.length, resultCount + 10))}>
            {'SHOW MORE'}
          </FilterOptionButton>}
        </FilterOptionsList>
      </>
    ) : null;
  };
  SearchFacet.propTypes = searchFacetPropTypes;

  const SearchResultsCount = () => {
    let currentResultsTotal = 0;
    currentResultsTotal = defaultSearchActive ? defaultResultsTotal : resultsTotal;

    return (
      <ResultsCountText>
        {loading
          ? 'Searching...'
          : resultsTotal == 0
          ? 'Showing 0 results'
          : `Showing ${(pageNumber - 1) * MAX_RESULTS_PER_PAGE + 1} - ${Math.min(
              pageNumber * MAX_RESULTS_PER_PAGE,
              currentResultsTotal
            )} of ${currentResultsTotal} results`}
      </ResultsCountText>
    );
  };

  const SearchResultsPage = () => {
    return (
      <SearchResultsContainer>
        {displayedResults?.map(r => (
          <SearchResultCard key={r.rawData.slug} result={r} />
        ))}
      </SearchResultsContainer>
    );
  };

  const SearchPagination = () => {
    let numPages = 0;
    numPages = defaultSearchActive ?
      Math.ceil(defaultResultsTotal / MAX_RESULTS_PER_PAGE) :
      Math.ceil(resultsTotal / MAX_RESULTS_PER_PAGE);

    const setPage = page => {
      if (page != pageNumber) {
        searchActions.setOffset((page - 1) * MAX_RESULTS_PER_PAGE);
        searchActions.executeVerticalQuery();

        setPageNumber(page);
        urlParams.set(searchUtils.URL_PARAMS_PAGE, page);
        history.pushState(null, '', '?' + urlParams.toString());

        // Scroll back up to the top of the search page
        if (typeof window !== 'undefined') {
          const scrollLocation = document.querySelector('div.scrollTag');

          window.scrollTo({
            top: scrollLocation.offsetTop - 150,
            left: 0,
            behavior: 'smooth'
          });
        }
      }
    };

    /**
     * Replicates the functionality of Yext's prebuilt Pagination component.
     * Displays a page number (i) if:
     * - i is equal to 1 or the last page number                                        [1 ... 9]
     * - i is between the current offset +- 1, inclusive                                [... 3 |4| 5 ...]
     * - i is less than the current offset and the offset - 3 = 1                       [1 2 3 |4| ...]
     * - i is greater than the current offset and the offset + 3 = the max page number  [... |6| 7 8 9]
     *
     * Here's a full example output:
     * |1| 2 ... 9
     * 1 |2| 3 ... 9
     * 1 2 |3| 4 ... 9
     * 1 2 3 |4| 5 ... 9
     * 1 ... 4 |5| 6 ... 9
     * 1 ... 5 |6| 7 8 9
     * 1 ... 6 |7| 8 9
     * 1 ... 7 |8| 9
     * 1 ... 8 |9|
     *
     * Special edge case with 7 pages: 1 2 3 |4| 5 6 7
     */
    const renderPageButtons = () => {
      const pageButtons = [];

      for (let i = 1; i <= numPages; i++) {
        if (
          i == 1 ||
          i == numPages ||
          (i >= pageNumber - 1 && i <= pageNumber + 1) ||
          (pageNumber - 3 == 1 && i < pageNumber) ||
          (pageNumber + 3 == numPages && i > pageNumber)
        ) {
          pageButtons.push(
            <PaginationButton
              key={i}
              buttonstyle={'secondary'}
              selected={i == pageNumber}
              onClick={() => {
                setPage(i);
              }}
            >
              <PaginationButtonLabel>{i}</PaginationButtonLabel>
            </PaginationButton>
          );
        } else if ((pageNumber - 3 > 1 && i == pageNumber - 2) || (pageNumber + 3 < numPages && i == pageNumber + 2)) {
          pageButtons.push(
            <PaginationButton key={i} buttonstyle={'secondary'} selected={i == pageNumber}>
              <PaginationButtonLabel>{'...'}</PaginationButtonLabel>
            </PaginationButton>
          );
        }
      }

      return pageButtons;
    };

    return (
      <>
        {numPages > 1 && (
          <PaginationContainer>
            <button
              onClick={() => {
                setPage(Math.max(1, pageNumber - 1));
              }}
            >
              <ChevronLeft size={24} color={`${colors.buttonDefault}`} />
            </button>
            {renderPageButtons()}
            <button
              onClick={() => {
                setPage(Math.min(numPages, pageNumber + 1));
              }}
            >
              <ChevronRight size={24} color={`${colors.buttonDefault}`} />
            </button>
          </PaginationContainer>
        )}
      </>
    );
  };

  const ExpandResults = () => {
    return (
      <PaginationContainer onClick={() => setDefaultSearchActive(false)}>
        <ExpandResultsText>Click here for more results</ExpandResultsText>
      </PaginationContainer>);
  };

  const SearchResultCard = ({ result }) => {
    const { rawData } = result;
    const { name, c_snippet: snippet, slug, c_fullSlug: fullSlug, c_gatsbyImageData: imageData } = rawData;
    const resultImage = imageData ? JSON.parse(imageData) : null;

    const useDefaultSearchImage = () => {
      const { placeholder } = useStaticQuery(graphql`
        query {
          placeholder: file(relativePath: { eq: "search/default-search-image.png" }) {
            childImageSharp {
              gatsbyImageData(layout: CONSTRAINED, width: 540)
            }
          }
        }
      `);
      return getImage(placeholder);
    };

    const hero = getImage(resultImage);

    return (
      <div onClick={() => reportSearchResultClick(result)}>
        <SearchCardWrapperLink
          to={
            result.entityType == searchUtils.ENTITY_TYPE_CASE_STUDY
              ? `/case-studies/${slug}`
              : result.entityType == searchUtils.ENTITY_TYPE_SERVICE
              ? `/services/${fullSlug ? fullSlug : slug}`
              : result.entityType == searchUtils.ENTITY_TYPE_INDUSTRY
              ? `/industries/${slug}`
              : result.entityType == searchUtils.ENTITY_TYPE_PARTNERSHIP
              ? `/partnerships/${slug}`
              : result.entityType == searchUtils.ENTITY_TYPE_INSIGHT
              ? `/insights/${slug}`
              : '/'
          }
        >
          <SearchCardWrapper>
            <SearchCardHeroGatsbyImg
              image={hero ?? useDefaultSearchImage()}
              objectFit={result.entityType == searchUtils.ENTITY_TYPE_PARTNERSHIP ? 'contain' : 'cover'}
              alt={'Search result hero'}
            ></SearchCardHeroGatsbyImg>
            <SearchCardContent>
              <SearchCardTitleHeading level={5}>{name}</SearchCardTitleHeading>
              <SearchCardSnippetText>{snippet}</SearchCardSnippetText>
            </SearchCardContent>
          </SearchCardWrapper>
        </SearchCardWrapperLink>
      </div>
    );
  };
  SearchResultCard.propTypes = searchResultCardPropTypes;

  const handleSearchBarFocus = () => {
    let vertical = searchUtils.VERTICAL_ALL_CONTENT
    setSearchActive(true);
    searchActions.setVertical(vertical);
  }

  const handleSearchBarBlur = () => {
    setSearchActive(false);
  }

  return (
    <SearchComponentWrapper className={'scrollTag'}>
      <SearchBarContainer onFocus={handleSearchBarFocus} onBlur={handleSearchBarBlur} >
        <CustomSearchBar handleSearch={handleSearch} />
      </SearchBarContainer>
      <BorderContainer>
        <BorderDiv />
      </BorderContainer>
      <SearchContainer>
        {query && (
          <SearchContent loading={+loading}>
            <FilterSection>
              <ResultsCountContainer>
                <SearchResultsCount />
                <Heading level={5}>
                  {`${
                    filterApplied == searchUtils.VERTICAL_CASE_STUDIES
                      ? 'Case study'
                      : filterApplied == searchUtils.VERTICAL_INSIGHTS
                      ? 'Insight'
                      : filterApplied == searchUtils.VERTICAL_SERVICES
                      ? 'Service'
                      : filterApplied == searchUtils.VERTICAL_INDUSTRIES
                      ? 'Industry'
                      : filterApplied == searchUtils.VERTICAL_PARTNERSHIPS
                      ? 'Partnership'
                      : 'All'
                  } results for "${query}"`}
                </Heading>
              </ResultsCountContainer>
              <FilterFacetsContainer>
                <SearchFilters />
                <SearchSort />
                <SearchFacet fieldId={`${searchUtils.FACET_CASE_STUDIES_CATEGORY}`} displayName="Category" toggleVisibility={toggleCategory} isVisible={isCategoryVisible} resultCount={categoryCount} setResultCount={setCategoryCount} />
                <SearchFacet fieldId={`${searchUtils.FACET_INSIGHTS_INSIGHT_TYPE}`} displayName="Insight Type" toggleVisibility={toggleInsightType} isVisible={isInsightTypeVisible} resultCount={insightTypeCount} setResultCount={setInsightTypeCount} />
                <SearchFacet fieldId={`${searchUtils.FACET_CASE_STUDIES_TOOL}`} displayName="Tool" toggleVisibility={toggleTool} isVisible={isToolVisible} resultCount={toolCount} setResultCount={setToolCount} />
              </FilterFacetsContainer>
            </FilterSection>

            {/* If no results exist for the current vertical, display a message in place of the results cards */}
            {!displayedResults && !loading ? (
              <NoResultsFoundContainer>
                <Text>{`No ${
                  filterApplied == searchUtils.VERTICAL_CASE_STUDIES
                    ? 'case study'
                    : filterApplied == searchUtils.VERTICAL_INSIGHTS
                    ? 'insight'
                    : filterApplied == searchUtils.VERTICAL_SERVICES
                    ? 'service'
                    : filterApplied == searchUtils.VERTICAL_INDUSTRIES
                    ? 'industry'
                    : filterApplied == searchUtils.VERTICAL_PARTNERSHIPS
                    ? 'partnership'
                    : ''
                } results for "${query}"`}</Text>
              </NoResultsFoundContainer>
            ) : (
              <SearchResultsSection>
                <SearchResultsPage />
                <SearchPagination />
                {defaultSearchActive && pageNumber === 5 && <ExpandResults />}
              </SearchResultsSection>
            )}
          </SearchContent>
        )}
      </SearchContainer>
    </SearchComponentWrapper>
  );
};

SearchComponent.propTypes = searchPropTypes;

const SearchComponentWrapper = styled.div``;

const SearchBarContainer = styled.div`
  padding-top: 40px;
  padding-bottom: 24px;
  width: 100%;
  display: flex;
  flex-direction: column;

  .search-bar-container {
    height: 26px;

    & > div {
      align-items: center;

      & > div:nth-child(1) {
        max-width: ${media.hugeBottom};
        margin: auto;
        padding: 0 80px;
      }

      .search-input-divider {
        max-width: ${media.largeTop};
        width: calc(100% - 160px);
      }

      & > div:nth-child(3) {
        width: 100%;
        max-width: ${media.hugeBottom};
        margin: auto;
        padding: 12px 80px;
      }
    }
  }

  @media screen and (max-width: ${media.mediumTop}) {
    padding-bottom: 20px;

    .search-bar-container {
      & > div {
        & > div:nth-child(1) {
          padding: 0 20px;
        }

        .search-input-divider {
          width: calc(100% - 40px);
        }

        & > div:nth-child(3) {
          padding: 12px 20px 0 20px;
        }
      }
    }
  }
`;

/**
 * Given how the searchbar is being styled on this page, this was a nice hacky way to add the divider
 * to the page to ensure it kept the correct width.
 */
const BorderContainer = styled(Container)`
  padding-top: 0;
  padding-bottom: 0;
`;

const BorderDiv = styled.div`
  background-color: ${colors.gray3};
  height: 1px;
`;

const SearchContainer = styled(Container)`
  min-height: calc(100dvh - 120px - 465px); // view height - header - footer
  display: flex;
  flex-direction: column;
  padding-top: 0;
  padding-bottom: 100px;

  @media only screen and (max-width: ${media.mediumBottom}) {
    padding-bottom: 88px;
  }

  @media only screen and (max-width: ${media.largeTop}) {
    min-height: calc(100dvh - 72px - 465px); // view height - header - footer
  }
`;

const NoResultsFoundContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchContent = styled.div`
  display: flex;
  gap: 84px;
  justify-content: space-between;
  margin-top: 60px;

  ${({ loading }) => (loading ? 'opacity: .5' : '')};

  @media only screen and (max-width: ${media.mediumBottom}) {
    flex-direction: column;
    gap: 28px;
    margin-top: 28px;
  }
`;

const ResultsCountText = styled(Text)`
  color: ${colors.gray1};
`;

const FilterSection = styled.div`
  width: 242px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media only screen and (max-width: ${media.mediumBottom}) {
    width: inherit;
    gap: 16px;
  }
`;

const FilterFacetsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media only screen and (max-width: ${media.mediumBottom}) {
    gap: 12px;
  }
`;

const ResultsCountContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  max-height: ${({ isVisible }) => (isVisible ? 'auto' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
  opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};

  @media only screen and (max-width: ${media.mediumBottom}) {
    flex-direction: row;
    align-items: center;
    overflow-x: scroll;

    // TODO: Works great but we lose the ability to scroll when in "desktop" mode if using a regular mouse, track pad works just fine. Are we concerned about this?
    // Hide Scrollbars But Keep Functionality
    ::-webkit-scrollbar {
      // Chrome, Safari, and Opera
      display: none;
    }
    -ms-overflow-style: none; // IE and Edge
    scrollbar-width: none; // Firefox
  }
`;

const FilterLabel = styled(Label)`
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  @media only screen and (max-width: ${media.mediumBottom}) {
    margin-right: 8px;
  }
`;

const FilterOptionButton = styled(Button)`
  padding: 8px 16px;
  border: 1px solid ${colors.gray3};
  border-radius: 8px;
  text-transform: none;
  text-align: left;
  background-color: ${({ selected }) => (selected ? `${colors.buttonHover}` : `${colors.pureWhite}`)};

  p {
    color: ${({ selected }) => (selected ? `${colors.pureWhite}` : `${colors.buttonDefault}`)};
  }

  @media only screen and (max-width: ${media.mediumBottom}) {
    white-space: nowrap;
  }
`;

const SearchResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  flex: 1;
`;

const SearchResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media only screen and (max-width: ${media.mediumBottom}) {
    gap: 20px;
  }
`;

const SearchCardWrapperLink = styled(InternalLink)`
  display: flex;
  text-decoration: none;
  text-transform: none;
  overflow: hidden;
  align-items: inherit;

  // Don't need top border on first result in tablet view
  @media only screen and (max-width: ${media.mediumTop}) {
    &:nth-of-type(n + 2) {
      border-top: 1px solid ${colors.gray3};
    }
  }

  @media only screen and (max-width: ${media.mediumBottom}) {
    border-top: 1px solid ${colors.gray3};
  }
`;

const SearchCardWrapper = styled.div`
  display: flex;
  gap: 32px;

  @media only screen and (max-width: ${media.mediumBottom}) {
    padding-top: 20px;
    gap: 12px;
  }
`;

const SearchCardHeroGatsbyImg = styled(GatsbyImage)`
  width: 270px;
  height: 180px;
  transition: transform ${animation.general.durationAndTiming};

  ${SearchCardWrapperLink}:hover & {
    transform: scale(1.035);
  }

  @media only screen and (max-width: ${media.mediumTop}) {
    display: none;
  }
`;

const SearchCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  padding-right: 32px;
  justify-content: center;
`;

const SearchCardTitleHeading = styled(Heading)`
  transition: color ${animation.general.durationAndTiming};

  ${SearchCardWrapperLink}:hover & {
    color: ${colors.linkHover};
  }
`;

const SearchCardSnippetText = styled(Text)``;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled(Button)`
  display: flex;
  width: 36px;
  padding: 4px 12px;
  border-radius: 4px;
  justify-content: center;
  border: none;
  background-color: ${({ selected }) => (selected ? `${colors.buttonHover}` : `${colors.pureWhite}`)};

  p {
    color: ${({ selected }) => (selected ? `${colors.pureWhite}` : `${colors.midnightBlack}`)};
  }

  &:hover {
    background-color: ${colors.buttonHover};

    p {
      transition: ${animation.general.transition};
      color: ${colors.pureWhite};
    }
  }
`;

const PaginationButtonLabel = styled(Text)``;

const ExpandResultsText = styled(Text)`
  color: ${colors.gray1};

  &:hover {
    color: ${colors.linkHover};
    cursor: pointer;
  }
`;

export default SearchComponent;
